import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  buildStatusDeltaMap,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import { queryPipelineStages } from '@/features/pipelines/data';
import {
  buildStageCounts,
  buildStageMetricCards,
} from '@/features/pipelines/hooks/useStagesPageViewModel.helpers';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import { buildDateRangeFilters, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { BuildStatus } from '@flowqa/shared';

type UseStageMetricCardsArgs = {
  isAuthenticated: boolean;
  showDelta: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  stageSearchValue: string;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  previousStartDay: string | null;
  previousEndDay: string | null;
  baseStageFilters: ReturnType<typeof mergeFilters>;
  currentStagesLength: number;
  currentStageExecutionsLength: number;
  previousStageExecutionsLength: number;
  currentStagesLoading: boolean;
  currentStageCounts: Partial<Record<BuildStatus, number>>;
  stageMetricOptions: Array<MetricCardOption<BuildStatus>>;
};

export const useStageMetricCards = ({
  isAuthenticated,
  showDelta,
  selectedOrganisationId,
  selectedProjectId,
  stageSearchValue,
  selectedPipelineIds,
  selectedStageNames,
  selectedStatuses,
  selectedTags,
  previousStartDay,
  previousEndDay,
  baseStageFilters,
  currentStagesLength,
  currentStageExecutionsLength,
  previousStageExecutionsLength,
  currentStagesLoading,
  currentStageCounts,
  stageMetricOptions,
}: UseStageMetricCardsArgs): { metricCards: ReturnType<typeof buildStageMetricCards> } => {
  const previousStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'rows',
      'previous',
      selectedOrganisationId,
      selectedProjectId,
      stageSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      previousStartDay,
      previousEndDay,
    ),
    enabled: isAuthenticated && showDelta && previousStartDay !== null && previousEndDay !== null,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(selectedProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: stageSearchValue,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: mergeFilters(
              baseStageFilters,
              buildDateRangeFilters(
                'startedAt',
                dayjs(previousStartDay).startOf('day').toISOString(),
                dayjs(previousEndDay).endOf('day').toISOString(),
              ),
            ),
          }),
      ),
  });

  const previousStageCounts = useMemo(
    () => buildStageCounts(previousStagesQuery.data?.items ?? []),
    [previousStagesQuery.data?.items],
  );

  const stageDeltas = useMemo(
    () => (showDelta ? buildStatusDeltaMap(currentStageCounts, previousStageCounts) : undefined),
    [currentStageCounts, previousStageCounts, showDelta],
  );

  const metricCards = useMemo(
    () =>
      buildStageMetricCards({
        options: stageMetricOptions,
        counts: currentStageCounts,
        deltas: stageDeltas,
        stageTotal: currentStagesLength,
        executionTotal: currentStageExecutionsLength,
        previousCounts: showDelta ? previousStageCounts : undefined,
        previousStageTotal: showDelta ? (previousStagesQuery.data?.items.length ?? 0) : undefined,
        previousExecutionTotal: showDelta ? previousStageExecutionsLength : undefined,
        loading: currentStagesLoading,
        showDelta,
      }),
    [
      currentStageCounts,
      currentStageExecutionsLength,
      currentStagesLength,
      currentStagesLoading,
      previousStageCounts,
      previousStageExecutionsLength,
      previousStagesQuery.data?.items.length,
      showDelta,
      stageDeltas,
      stageMetricOptions,
    ],
  );

  return { metricCards };
};
